/**
 * Brand2025 Story 动画优化版本
 * 实现丝滑的动画效果，避免卡顿
 */
(function() {
    'use strict';

    // 窗口大小管理
    const windowManager = {
        windowSize: {
            width: window.innerWidth || document.documentElement.clientWidth,
            height: window.innerHeight || document.documentElement.clientHeight
        },
        maxHeight: null,
        
        // 初始化窗口大小
        init() {
            this.updateSize();
            // 设置初始maxHeight
            this.maxHeight = this.windowSize.height;
        },
        
        // 更新窗口大小
        updateSize() {
            this.windowSize.width = window.innerWidth || document.documentElement.clientWidth;
            this.windowSize.height = window.innerHeight || document.documentElement.clientHeight;
            
            // 移动端特殊处理：处理地址栏隐藏/显示的问题
            // 使用与main-v5.js相同的设备检测逻辑
            const isMobile = this.windowSize.width <= 768;
            if (isMobile) {
                // 如果当前高度大于maxHeight，更新maxHeight
                if (this.maxHeight === null || this.windowSize.height > this.maxHeight) {
                    this.maxHeight = this.windowSize.height;
                }
            } else {
                // PC端直接使用当前高度
                this.maxHeight = this.windowSize.height;
            }
        },
        
        // 获取有效的视口高度
        getEffectiveHeight() {
            return this.maxHeight || this.windowSize.height;
        },
        
        // 获取有效的视口宽度
        getEffectiveWidth() {
            return this.windowSize.width;
        },
        
        // 检查是否为移动设备（避免循环依赖）
        isMobile() {
            return this.windowSize.width <= 768;
        },
        
        // 检查是否为PC设备
        isPc() {
            return this.windowSize.width > 1024;
        },
        
        // 检查是否为移动设备（兼容main-v5.js中的isMo方法）
        isMo() {
            return this.windowSize.width <= 768;
        },
        
        // 检查是否为平板设备
        isPad() {
            return this.windowSize.width > 768 && this.windowSize.width <= 1024;
        }
    };

    // 动画属性设置系统 - 与main-v5.js功能一致
    const animationProps = {
        // 常量定义
        TRANSFORM: "transform",
        TRANSFORM_ORIGIN: "transformOrigin",
        PI: Math.PI,
        DEG_TO_RAD: Math.PI / 180,
        RAD_TO_DEG: 180 / Math.PI,
        
        // 属性映射
        propertyMap: {
            autoAlpha: "opacity,visibility",
            scale: "scaleX,scaleY",
            alpha: "opacity"
        },
        
        // 获取计算样式
        getComputedStyle: (element, property) => {
            const style = window.getComputedStyle(element);
            return style[property] || style.getPropertyValue(property.replace(/([A-Z])/g, "-$1").toLowerCase()) || style.getPropertyValue(property) || "";
        },
        
        // 设置样式属性
        setStyle: (element, property, value) => {
            if (element && element.style) {
                element.style[property] = value;
            }
        },
        
        // 设置CSS属性
        setCSSProperty: (element, property, value) => {
            if (element && element.style) {
                element.style.setProperty(property, value);
            }
        },
        
        // 移除样式属性
        removeStyle: (element, property) => {
            if (element && element.style) {
                element.style.removeProperty(property);
            }
        },
        
        // 获取元素属性值
        getPropertyValue: (element, property, unit) => {
            const value = this.getComputedStyle(element, property);
            if (unit && value) {
                return this.convertUnit(element, property, value, unit);
            }
            return value;
        },
        
        // 单位转换
        convertUnit: (element, property, value, targetUnit) => {
            const num = parseFloat(value) || 0;
            const currentUnit = (value + "").trim().substr((num + "").length) || "px";
            
            if (targetUnit === currentUnit) {
                return num;
            }
            
            // 简单的单位转换逻辑
            if (currentUnit === "px" && targetUnit === "%") {
                const parent = element.parentElement;
                if (parent) {
                    const parentSize = property.includes("width") ? parent.offsetWidth : parent.offsetHeight;
                    return parentSize > 0 ? (num / parentSize) * 100 : 0;
                }
            } else if (currentUnit === "%" && targetUnit === "px") {
                const parent = element.parentElement;
                if (parent) {
                    const parentSize = property.includes("width") ? parent.offsetWidth : parent.offsetHeight;
                    return (num / 100) * parentSize;
                }
            }
            
            return num;
        },
        
        // 设置变换属性
        setTransform: (element, property, value) => {
            if (!element || !element.style) return;
            
            if (property === "transform") {
                element.style.transform = value;
            } else if (property === "transformOrigin") {
                element.style.transformOrigin = value;
            } else if (this.propertyMap[property]) {
                const properties = this.propertyMap[property].split(",");
                properties.forEach(prop => {
                    this.setStyle(element, prop.trim(), value);
                });
            } else {
                this.setStyle(element, property, value);
            }
        },

        // 批量设置变换属性
        setTransformProperties: (element, properties) => {
            if (!element) return;
            
            Object.keys(properties).forEach(property => {
                this.setTransform(element, property, properties[property]);
            });
        },
        
        // 批量设置属性
        setProperties: (element, properties) => {
            if (!element) return;
            
            Object.keys(properties).forEach(property => {
                this.setTransform(element, property, properties[property]);
            });
        },
        
        // 创建属性设置器
        createPropertySetter: (element, properties) => {
            return {
                target: element,
                properties: properties,
                set: (props) => {
                    this.setProperties(element, props);
                },
                get: (property) => {
                    return this.getPropertyValue(element, property);
                }
            };
        }
    };

    // 异步生成器包装器 - 与main-v5.js功能一致
    const asyncGeneratorWrapper = (context, args, generator) => new Promise((resolve, reject) => {
        const handleNext = (value) => {
            try {
                handleResult(generator.next(value));
            } catch (error) {
                reject(error);
            }
        };
        
        const handleThrow = (error) => {
            try {
                handleResult(generator.throw(error));
            } catch (err) {
                reject(err);
            }
        };
        
        const handleResult = (result) => {
            if (result.done) {
                resolve(result.value);
            } else {
                Promise.resolve(result.value).then(handleNext, handleThrow);
            }
        };
        
        handleResult((generator = generator.apply(context, args)).next());
    });

    // 插件变量系统 - 与main-v5.js功能一致
    const plgs = {
        variables: {
            // 获取导航栏高度
            getNavHeight: () => {
                const navbar = document.querySelector('nav, .navbar, .header');
                return navbar ? navbar.offsetHeight : 0;
            },
            // 获取内容高度
            getContentHeight: () => {
                return document.documentElement.scrollHeight;
            },
            // 获取网格宽度
            getGridWidth: () => {
                const grid = document.querySelector('.grid');
                return grid ? grid.offsetWidth : 0;
            }
        }
    };

    // 工具函数
    const utils = {
        // 获取视口高度
        vh: (value) => windowManager.getEffectiveHeight() * value / 100,
        // 获取视口宽度
        vw: (value) => windowManager.windowSize.width * value / 100,
        // 动态计算vh值（像素），与main-v5.js的C.vh函数功能一致
        // 使用示例: utils.calculateVH(100) 返回100vh对应的像素值
        // 兼容移动端地址栏变化，与main-v5.js中的(0, C.vh)(100)效果一致
        calculateVH: (percentage) => {
            return windowManager.getEffectiveHeight() * percentage / 100;
        },
        // 动态计算vw值（像素） - 修复：使用与main-v5.js相同的宽度获取方式
        calculateVW: (percentage) => {
            // 使用与main-v5.js相同的宽度获取逻辑
            const width = document.documentElement.clientWidth || window.innerWidth;
            return width * percentage / 100;
        },
        // 检测是否为移动设备
        isMobile: () => windowManager.isMobile(),
        // 检测是否为移动设备（兼容main-v5.js中的isMo方法名）
        isMo: () => windowManager.isMo(),
        // 检测是否为平板
        isPad: () => windowManager.isPad(),
        // 检测是否为PC
        isPc: () => windowManager.isPc(),
        // 节流函数
        throttle: (func, limit) => {
            let inThrottle;
            return function() {
                const args = arguments;
                const context = this;
                if (!inThrottle) {
                    func.apply(context, args);
                    inThrottle = true;
                    setTimeout(() => inThrottle = false, limit);
                }
            }
        },
        // 防抖函数
        debounce: (func, wait) => {
            let timeout;
            return function executedFunction(...args) {
                const later = () => {
                    clearTimeout(timeout);
                    func(...args);
                };
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            };
        },
        // 获取元素
        query: (selector, parent = document) => parent.querySelector(selector),
        queryAll: (selector, parent = document) => parent.querySelectorAll(selector),
        // 添加CSS类
        addClass: (element, className) => element.classList.add(className),
        removeClass: (element, className) => element.classList.remove(className),
        // 设置CSS属性
        setStyle: (element, property, value) => element.style.setProperty(property, value),
        // 获取元素位置信息
        getElementRect: (element) => element.getBoundingClientRect(),
        // 检查元素是否在视口中
        isInViewport: (element) => {
            const rect = element.getBoundingClientRect();
            return (
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                rect.right <= (window.innerWidth || document.documentElement.clientWidth)
            );
        },
        // 检查是否为RTL布局
        isRTL: () => {
            return document.documentElement.dir === 'rtl' || 
                   document.body.dir === 'rtl' || 
                   getComputedStyle(document.documentElement).direction === 'rtl';
        },
        // 切换CSS类
        toggleClass: (element, className, force) => {
            if (element && element.classList) {
                if (force !== undefined) {
                    element.classList.toggle(className, force);
                } else {
                    element.classList.toggle(className);
                }
            }
        },
        // 添加窗口大小变化监听器
        addResizeListener: (callback) => {
            window.addEventListener('resize', callback);
        },
        // 获取元素顶部位置（与main-v5.js的top方法一致）
        top: (element) => {
            const docRect = document.documentElement.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            const transform = window.getComputedStyle(element).getPropertyValue('transform').match(/matrix\(.*,\s+([\d\.]+)\)$/);
            return !transform || !transform[1] || !parseInt(transform[1]) ? 
                elementRect.top - docRect.top : 
                elementRect.top - docRect.top - parseInt(transform[1]);
        },
        // 获取元素底部位置
        bottom: (element) => {
            return utils.top(element) + element.offsetHeight;
        },
        // 获取元素中心位置
        center: (element) => {
            return utils.top(element) + element.offsetHeight / 2;
        },
        // resizeRef 方法（与main-v5.js一致）
        resizeRef: (callback) => {
            return callback;
        },
        // b9 方法（与main-v5.js的C.b9函数功能一致，检测是否为PC）
        b9: () => {
            return utils.isPc();
        },
        // 插件变量系统
        plgs: plgs,
        // 异步生成器包装器
        asyncGeneratorWrapper: asyncGeneratorWrapper,
        // 动画属性设置系统
        animationProps: animationProps
    };

    // 扩展HTMLElement原型，与main-v5.js保持一致
    if (!HTMLElement.prototype.top) {
        HTMLElement.prototype.top = HTMLElement.prototype._top = function() {
            const docRect = document.documentElement.getBoundingClientRect();
            const elementRect = this.getBoundingClientRect();
            const transform = window.getComputedStyle(this).getPropertyValue('transform').match(/matrix\(.*,\s+([\d\.]+)\)$/);
            return !transform || !transform[1] || !parseInt(transform[1]) ? 
                elementRect.top - docRect.top : 
                elementRect.top - docRect.top - parseInt(transform[1]);
        };
    }

    if (!HTMLElement.prototype._height) {
        HTMLElement.prototype._height = function() {
            return this.offsetHeight;
        };
    }

    if (!HTMLElement.prototype._width) {
        HTMLElement.prototype._width = function() {
            return this.offsetWidth;
        };
    }

    if (!HTMLElement.prototype.bottom) {
        HTMLElement.prototype.bottom = HTMLElement.prototype._bottom = function() {
            return this._top() + this._height();
        };
    }

    if (!HTMLElement.prototype.center) {
        HTMLElement.prototype.center = HTMLElement.prototype._center = function() {
            return this._top() + this._height() / 2;
        };
    }

    // 动画管理器
    class AnimationManager {
        constructor() {
            this.animations = new Map();
            this.isInitialized = false;
            this.init();
        }

        init() {
            if (this.isInitialized) {
                return;
            }
            
            
            // 页面刷新时滚动条保持在顶部
            this.ensureScrollToTop();
            
            // 等待DOM加载完成
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => {
                    // console.log('DOMContentLoaded 触发');
                    setTimeout(() => this.setupAnimations(), 100);
                });
            } else {
                // console.log('DOM 已加载完成');
                setTimeout(() => this.setupAnimations(), 100);
            }
            
            // 额外确保在窗口完全加载后初始化
            window.addEventListener('load', () => {
                // console.log('Window load 触发');
                if (!this.isInitialized) {
                    setTimeout(() => this.setupAnimations(), 200);
                }
            });
        }

        setupAnimations() {
            // console.log('setupAnimations 开始执行');
            this.isInitialized = true;
            
            // 初始化vh值
            this.updateVHValues();
            
            // 初始化各个动画模块
            setTimeout(() => {
                this.initKVAnimation();
                this.initBoomAnimation();
                this.initPicsAnimation();
                this.initVideoSwitcher();
            }, 50);
            
            // 初始化窗口管理器
            windowManager.init();
            
            // 刷新所有 ScrollTrigger
            if (typeof ScrollTrigger !== 'undefined') {
                setTimeout(() => {
                    ScrollTrigger.refresh();
                    // console.log('ScrollTrigger 已刷新');
                }, 100);
            }
            
            // 添加窗口大小变化监听
            window.addEventListener('resize', utils.debounce(() => {
                // 更新窗口大小
                windowManager.updateSize();
                // 刷新动画
                this.refreshAnimations();
                // 重新计算vh值并更新CSS变量
                this.updateVHValues();
            }, 250));
        }


        initKVAnimation() {
            // 变量定义 - 与main-v5.js完全一致
            const kvContent = document.querySelector('.kv-content');
            const nowTextElement = utils.query('.now-text-element');
            const kvContainer = document.querySelector('.kv-container');
            const kvText = document.querySelector('.kv-text');
            const moveBox = document.querySelector('.move-box');
            const fadeText = document.querySelector('.fade-text');
            const videoContainer = document.querySelector('.video-container');
            const nowTextContainer = utils.query('.now-text-container');
            const playBtnContainer = utils.query('.play-btn-container');
            const desc = utils.query('[data-desc]');
            
            // 设置play-btn-container的offset属性
            if (playBtnContainer) {
                playBtnContainer.style.setProperty("--offset", 0 + "px");
            }
            
            const boomStickyContainer = utils.query('.boom-sticky-container');
            const textContainerWrapper = document.querySelector('.text-container-wrapper');

            // 等待字体加载完成
            document.fonts.ready.then(() => {
                // console.log("fonts ready");
            });

            // 第一个timeline - 视频容器动画
            gsap.timeline({
                defaults: {
                    ease: "none"
                },
                scrollTrigger: {
                    trigger: ".kv-content",
                    start: "top top",
                    end: "bottom top",
                    scrub: 0.1
                }
            }).fromTo(".video-container", {
                y: 0
            }, {
                y: "30vh"
            }, "0").fromTo(moveBox, {
                y: 0
            }, {
                y: "-100vh",
                onComplete() {}
            }, 0).fromTo(".video-player", {
                borderRadius: "0px",
                scaleX: 1,
                transformOrigin: "center center"
            }, {
                borderRadius: "20px",
                scaleX: 0.9725,
                transformOrigin: "center center"
            }, "0");

            // 第二个timeline - 文字动画 - 与main-v5.js完全一致
            gsap.timeline({
                defaults: {},
                scrollTrigger: {
                    trigger: ".kv-content",
                    start: "top top",
                    end: `+=${kvContent.clientHeight * 1}`,
                    scrub: true
                }
            }).fromTo(desc, {
                opacity: 1,
                scale: 1
            }, {
                opacity: 0,
                height: 0,
                scale: 0.9,
                duration: utils.isPc() ? 0.25 : 0.3
            },  0).fromTo(fadeText, {
                y: utils.isPc() ? utils.calculateVH(60) : utils.calculateVH(90)
            }, {
                y: 0
            }, "0").fromTo(kvText, {
                scale: 1,
                transformOrigin: "bottom",
            }, {
                scale: utils.isPc() ? 0.374 : 0.467,
            }, 0);
        
            // 第三个timeline - 文字容器动画 - 与main-v5.js完全一致
            const textTimeline = gsap.timeline({
                defaults: {
                    ease: "none"
                },
                scrollTrigger: {
                    trigger: ".kv-content",
                    start: "top+=100% top",
                    endTrigger: ".boom-sticky-container",
                    end: "top top",
                    scrub: true
                }
            }).fromTo(".text-container-wrapper", {
                opacity: 1
            }, {
                opacity: 0,
                onReverseComplete() {
                    // 注意：这里需要定义变量a，对应main-v5.js中的a
                    const a = document.querySelector('.kv-container');
                    if (a) {
                        a.style.pointerEvents = "auto";
                    }
                    // console.log("onComplete");
                },
                onComplete() {
                    const a = document.querySelector('.kv-container');
                    if (a) {
                        a.style.pointerEvents = "none";
                    }
                    // console.log("onComplete");
                },
                duration: 0.05
            }, 0.45).fromTo(".brand-text", {
                opacity: 0
            }, {
                opacity: 1,
                duration: 0.05
            }).fromTo(".brand-sticky-container", {
                opacity: 1
            }, {
                opacity: 0,
                duration: 0.1
            }, 0.9);
            // 移动端resize监听器 - 与main-v5.js完全一致
            if (!utils.isPc()) {
                window.addEventListener('resize', utils.debounce(() => {
                    if (textTimeline.scrollTrigger) {
                        textTimeline.scrollTrigger.refresh();
                    }
                }, 250));
            }

            // 处理 .moment-gtm 和 .core-gtm 元素的动态定位
            // const momentGtm = utils.query(".moment-gtm");
            // const coreGtm = utils.query(".core-gtm");
            
            // if (momentGtm && kvTimeline && kvTimeline.scrollTrigger) {
            //     // 添加resize监听器来动态调整元素位置
            //     window.addEventListener('resize', utils.debounce(() => {
            //         if (kvTimeline.scrollTrigger) {
            //             const scrollTriggerHeight = kvTimeline.scrollTrigger.end - kvTimeline.scrollTrigger.start;
            //             momentGtm.style.top = kvTimeline.scrollTrigger.start + "px";
            //             momentGtm.style.height = (scrollTriggerHeight * 0.5) + "px";
            //         }
            //     }, 250));
            // }
        
            // now-text-element img 动画
            if (nowTextElement) {
                const img1 = nowTextElement.querySelector('img:first-child');
                const img2 = nowTextElement.querySelector('img:last-child');
                gsap.fromTo(img1, { opacity: 0 }, { opacity: 1, y: 0, duration: 0.1, delay: 0.5 });
                gsap.fromTo(img2, { width: 0 }, { width: "auto", duration: 0.4, delay: 1 });
                gsap.fromTo(img2, { opacity: 0 }, { opacity: 1, duration: 0.1, delay: 1.4 });
            }

            if (desc) {
                gsap.fromTo(desc, { opacity: 0 }, { opacity: 1, duration: 0.4, delay: 1.8 });
            }
        
            // play-btn-container 动画
            if (playBtnContainer) {
                gsap.fromTo(playBtnContainer, { opacity: 0 }, { opacity: 1, duration: 0.1, delay: 0.5 });
            }
        
            // 字母逐个淡入动画 - 使用动态vh计算，类似main-v5.js中的实现
            document.querySelectorAll('[anim-text-letter-fade-reveal]').forEach(el => {
                const spans = el.querySelectorAll('.text-element span');
                if (spans.length > 0) {
                    gsap.fromTo(spans, {
                        opacity: 0
                    }, {
                        opacity: 1,
                        ease: "power1.out",
                        stagger: 0.015,
                        duration: 1.2,
                        scrollTrigger: {
                            trigger: el,
                            start: `top bottom-=${utils.calculateVH(10)}`,
                            end: `+=${utils.calculateVH(100)}`,
                            scrub: true
                        }
                    });
                }
            });
        }

        // Boom区域动画
        initBoomAnimation() {
            // console.log('initBoomAnimation 开始执行');
            
            // 检查 GSAP 和 ScrollTrigger 是否可用
            if (typeof gsap === 'undefined') {
                // console.error('GSAP 未加载');
                return;
            }
            
            if (typeof ScrollTrigger === 'undefined') {
                // console.error('ScrollTrigger 未加载');
                return;
            }
            
            // 检查页面结构
            // console.log('检查页面结构:');
            // console.log('- .kv-content:', document.querySelector('.kv-content'));
            // console.log('- [ani-boom]:', document.querySelector('[ani-boom]'));
            // console.log('- .content--spacing:', document.querySelectorAll('.content--spacing').length);
            // console.log('- .grid__item:', document.querySelectorAll('.grid__item').length);
            // console.log('- .grid__item-inner:', document.querySelectorAll('.grid__item-inner').length);
            
            const boomElement = utils.query('[ani-kv]');
            if (!boomElement) {
                // console.log('未找到 [ani-boom] 元素');
                return;
            }

            // 检查 .grid 元素是否在 [ani-boom] 内部
            let grid = utils.query('.grid', boomElement);
            if (!grid) {
                // 如果不在内部，尝试在整个文档中查找
                grid = utils.query('.grid');
                // console.log('在 [ani-boom] 内部未找到 .grid，尝试全局查找:', grid);
            }
            
            const boomContainer = utils.query('.boom-container', boomElement);
            const boomStickyContainer = utils.query('.boom-sticky-container', boomElement);

            if (!grid) {
                // console.log('未找到 .grid 元素');
                return;
            }

            // 设置网格属性
            const setGridProperties = () => {
                utils.setStyle(grid, '--grid-width', '105%');
                utils.setStyle(grid, '--grid-columns', '8');
                utils.setStyle(grid, '--perspective', '1500px');
                utils.setStyle(grid, '--grid-inner-scale', '1');
                utils.setStyle(grid, '--grid-height', 'auto');
                utils.setStyle(grid, '--grid-gap', '2vw');
                utils.setStyle(grid, '--grid-item-ratio', '1.5');
                
                if (boomContainer) {
                    utils.setStyle(boomContainer, '--grid-height', grid.clientHeight + 'px');
                }
                if (boomStickyContainer) {
                    utils.setStyle(boomStickyContainer, '--grid-height', grid.clientHeight + 'px');
                }
            };

            setGridProperties();

            const gridWrapper = utils.query('.grid-wrapper', grid);
            const gridItems = utils.queryAll('.grid__item', grid);
            // 将 NodeList 转换为数组，然后映射
            const gridItemInners = Array.from(gridItems).map(item => utils.query('.grid__item-inner', item));
            const gridImages = utils.queryAll('.grid__item-inner img', grid);
            
            // console.log('找到的 grid 元素:', grid);
            // console.log('grid 的父元素:', grid?.parentElement);
            // console.log('grid 的类名:', grid?.className);
            // console.log('gridItems 类型:', typeof gridItems, gridItems.constructor.name);
            // console.log('gridItems 长度:', gridItems.length);
            
            // console.log('找到的元素:', {
            //     gridWrapper: !!gridWrapper,
            //     gridItems: gridItems.length,
            //     gridItemInners: gridItemInners.length,
            //     gridImages: gridImages.length
            // });
            
            // 检查 grid 内部结构
            // console.log('grid 内部元素:', grid?.children);
            // console.log('grid 内部 .grid-wrapper:', grid?.querySelector('.grid-wrapper'));
            // console.log('grid 内部 .grid__item:', grid?.querySelectorAll('.grid__item').length);

            // 动态计算 gridWrapper 的初始值
            const calculateInitialZValue = () => {
                
                if (utils.isPc()) {
                    // console.log('PC端 713px ---------');
                    // PC端：713px
                    return 713;
                } else if (utils.isPad()) {
                    // console.log('平板端 1143px ---------');
                    // 平板端：1143px
                    return 1143;
                } else {
                    // console.log('移动端 1110px ---------');
                    // 移动端：1110px
                    return 1110;
                }
            };

            // 设置 gridWrapper 的初始值
            const setGridWrapperInitialValue = () => {
                if (gridWrapper) {
                    const initialZ = calculateInitialZValue();
                    // console.log(`设置 gridWrapper 初始值: ${initialZ}px (屏幕宽度: ${window.innerWidth}px)`);
                    
                    // 使用 GSAP 设置初始值，确保与动画系统兼容
                    gsap.set(gridWrapper, {
                        z: initialZ,
                    });
                    // console.log('gridWrapper 初始值:', gridWrapper.style.z);
                }
            };

            // 初始设置
            setGridWrapperInitialValue();

            // 预定义的动画数据（与main-v5.js完全一致）
            const zValues = [-3200.35064, -2559.08714, -2775.71775, -4815.29581, -3814.68899, -3583.51753, -2463.66212, -3916.04496, -2486.30334, -3390.29408, -4233.88417, -4766.61891, -3042.12798, -4801.83282, -4864.305];
            const rotationXValues = [-58.06434, -25.24322, -40.85629, -35.12773, -41.12393, -34.94301, -47.25802, -51.41291, -31.94523, -49.66937, -58.01767, -42.61762, -50.03867, -37.47516, -33.48596];
            const xPercentValues = [198.50544, 516.7302, 1458.74411, -340.10253, 328.16992, 551.22075, -226.66366, -376.27248, 312.19732, -402.23627, 404.89015, -396.16244, -427.1255, -800.58326, -1006.85337];
            const yPercentValues = utils.isMo() ? 
                [-403.06147, 632.46917, 694.29357, -976.95668, -849.48436, -503.06147, 432.46917, 494.29357, -876.95668, -249.48436, 221.19232, 120.47313, -113.49692, 100.61608, -200.2815, -162.45049, 65.97466, 179.08119, 104.16415, 577.43862] :
                [-403.06147, 632.46917, 694.29357, -776.95668, -449.48436, -203.06147, 432.46917, 494.29357, -576.95668, -249.48436, 221.19232, 120.47313, -113.49692, 100.61608, -200.2815, -162.45049, 65.97466, 179.08119, 104.16415, 577.43862];

            // 鼠标移动效果（仅PC） - 与main-v5.js完全一致
            const handleMouseMove = (event) => {
                const { clientX, clientY } = event;
                const xPercent = (clientX / window.innerWidth - 0.5) * 100;
                const yPercent = (clientY / window.innerHeight - 0.5) * 100;

                gsap.to(gridItemInners, {
                    x: xPercent,
                    y: yPercent,
                    duration: 1
                });
            };

            // 只在PC端添加鼠标移动监听器
            if (utils.isPc()) {
                window.addEventListener('mousemove', handleMouseMove);
            }

            // 创建滚动动画时间轴 - 与main-v5.js完全一致
            // console.log('创建 boomTimeline');
            // console.log('GSAP 版本:', gsap.version);
            // console.log('ScrollTrigger 版本:', ScrollTrigger.version);
            
            const boomTimeline = gsap.timeline({
                defaults: {
                    ease: "none"
                },
                scrollTrigger: {
                    trigger: ".kv-content",
                    start: () => utils.isPc() ? "top-=60% top" : `top-=${utils.calculateVH(100)} top`,
                    end: () => utils.calculateVH(450),
                    scrub: 0.1,
                    onUpdate: (self) => {
                        // console.log('ScrollTrigger 更新:', self.progress);
                    },
                    onRefresh: () => {
                        // console.log('ScrollTrigger 已刷新');
                    },
                    onRefreshInit: () => {
                        // console.log('ScrollTrigger 初始化刷新');
                    }
                }
            }).set(gridItems, {
                transformOrigin: "50% 50%",
                z: (index) => zValues[index],
                rotationX: (index) => rotationXValues[index]
            });

            // 主要动画序列
            // console.log('添加主要动画序列');
            // console.log('gridItems 数量:', gridItems.length);
            // console.log('gridWrapper 高度:', gridWrapper?.clientHeight);
            // console.log('content--spacing 元素:', document.querySelector('.content--spacing'));
            
            boomTimeline.to(gridItems, {
                xPercent: (index) => xPercentValues[index],
                yPercent: (index) => yPercentValues[index],
                rotationX: 0
            }, 0).fromTo(".content--spacing", {
                y: () => `${window.innerHeight - gridWrapper.clientHeight / 2}px`
            }, {
                y: () => 0
            }, utils.isMo() ? 0.2 : 0);

            // PC端设置初始亮度
            if (utils.isPc()) {
                boomTimeline.set(gridItems, {
                    filter: "brightness(20%)"
                });
            }

            // PC端亮度恢复
            if (utils.isPc()) {
                boomTimeline.to(gridItems, {
                    filter: "brightness(100%)",
                    duration: 0.2
                }, 0);
            }

            // 网格包装器Z轴动画 - 从初始值动画到6500
            boomTimeline.to(gridWrapper, {
                z: 6500
            }, utils.isMo() ? 0.2 : 0);

            this.animations.set('boom', boomTimeline);
            // console.log('boomTimeline 已添加到动画管理器');

            // 添加窗口大小变化监听器，重新计算初始值
            window.addEventListener('resize', utils.debounce(() => {
                setGridWrapperInitialValue();
                if (boomTimeline.scrollTrigger) {
                    boomTimeline.scrollTrigger.refresh();
                }
            }, 250));
        }

        // Pics区域动画
        initPicsAnimation() {
            // console.log('开始初始化Pics动画');
            
            const picsElement = utils.query('[ani-pics]');
            // console.log('picsElement 找到', picsElement);
            if (!picsElement) {
                //  console.log('未找到 [ani-pics] 元素');
                return;
            }

            const pics = utils.query('#pics');
            // console.log('pics 找到', pics);
            if (!pics) {
                // console.log('未找到 #pics 元素');
                return;
            }
            
            const picsListItems = Array.from(utils.queryAll('.pics-list-item', picsElement));
            // console.log('picsListItems 数量:', picsListItems.length);
            if (picsListItems.length === 0) {
                // console.log('未找到 .pics-list-item 元素');
                return;
            }
            
            const sectionBoom = utils.query('#section-boom');
            // console.log('sectionBoom 找到', sectionBoom);
            const picsContent = utils.query('.pics-content', picsElement);
            // console.log('picsContent 找到', picsContent);
            const boomStickyContainer = utils.query('.boom-sticky-container');
            // console.log('boomStickyContainer 找到', boomStickyContainer);
            const picsTexts = utils.queryAll('.pics-list-item-inner-text', picsElement);
            // console.log('picsTexts 数量:', picsTexts.length);
            const picsVideos = utils.queryAll('.pics-video-container video', picsElement);
            // console.log('picsVideos 数量:', picsVideos.length);

            // 检查GSAP和ScrollTrigger是否可用
            if (typeof gsap === 'undefined') {
                // console.error('GSAP 未加载');
                return;
            }
            
            if (typeof ScrollTrigger === 'undefined') {
                // console.error('ScrollTrigger 未加载');
                return;
            }
            
            // console.log('GSAP 和 ScrollTrigger 已加载');

            // 移动端隐藏视频
            // if (utils.isMobile()) {
            //     picsVideos.forEach(video => {
            //         utils.addClass(video, 'pc-hidden');
            //     });
            // }

            const depth = utils.isPc() ? 1800 : utils.isPad() ? 800 : utils.calculateVW(200);
            // console.log('动画深度:', depth);

            // 创建移动端动画时间轴
            const createMobileTimeline = () => {
                // 设置移动端初始状态：所有picsListItems的opacity为0
                picsListItems.forEach((item) => {
                    if (item) {
                        gsap.set(item, { opacity: 0 });
                    }
                });

                const timeline = gsap.timeline({
                    defaults: {
                        ease: 'none'
                    }
                });

                const ofWrappers = Array.from(utils.queryAll('.of-wrapper'));
                const offsets = [
                    [ofWrappers[0]?.clientWidth / 2 || 0, -utils.calculateVW(40)],
                    [-utils.calculateVW(15), utils.calculateVW(20)],
                    [utils.calculateVW(15), -utils.calculateVW(20)],
                    [-utils.calculateVW(15), utils.calculateVW(20)],
                    [0, ofWrappers[4]?.clientWidth / 2 || 0]
                ];

                picsListItems.forEach((item, index) => {
                    // console.log('---- item: 调试位置', item);
                    const innerImg = utils.query('.pics-list-item-inner-img', item);
                    if (!innerImg) return;

                    utils.setStyle(item, '--pics-item-index', (100 - index).toString());

                    timeline.fromTo(item, {
                        z: () => -1800,
                        x: () => offsets[index]?.[0] || 0
                    }, {
                        z: () => index === picsListItems.length - 1 ? 0 : depth,
                        x: () => offsets[index]?.[1] || 0,
                        duration: 0.25
                    }, index === 0 ? 0 : '<80%');

                    timeline.fromTo(item, {
                        opacity: 0
                    }, {
                        duration: 0.15,
                        opacity: 1
                    }, '<');
                });

                const scrollTrigger = ScrollTrigger.create({
                    trigger: sectionBoom,
                    start: 'top top',
                    end: `+=${picsListItems.length * utils.calculateVH(50)}`,
                    animation: timeline,
                    scrub: 1,
                    onUpdate: (self) => {
                        this.updatePicsProgress(self.progress);
                        updateBackgroundImages();
                    }
                });

                // 添加窗口大小变化监听
                utils.addResizeListener(() => {
                    scrollTrigger.refresh();
                });

                return { timeline, scrollTrigger };
            };

            // 背景图片动画相关变量
            const ofWrappers = Array.from(utils.queryAll('.of-wrapper'));
            const homeBgWorkItems = Array.from(utils.queryAll('.home-bg-work-item'));
            let currentIndex = -1;
            let currentProgress = 0;
            const picsInnerImgs = picsListItems.map(item => utils.query('.pics-list-item-inner-img', item));
            const targetOpacity = 1;
            const itemCount = picsListItems.length;

            // 验证必要的元素是否存在
            if (!homeBgWorkItems || homeBgWorkItems.length === 0) {
                //  console.warn('背景工作项不存在，跳过背景图片动画初始化');
            } else {
                // console.log(`找到 ${homeBgWorkItems.length} 个背景工作项`);
            }

            // 更新背景图片的函数
            const updateBackgroundImages = () => {
                if (!homeBgWorkItems || homeBgWorkItems.length === 0) {
                    //  console.warn('背景工作项不存在');
                    return;
                }

                let newIndex = -1;
                let minDistance = Infinity;

                picsListItems.forEach((item, index) => {
                    if (!item) return;
                    
                    const zValue = Number(gsap.getProperty(item, 'z'));
                    const normalizedZ = gsap.utils.normalize(-3000, 0, zValue);
                    
                    // 只在PC端设置picsListItems的opacity，移动端由动画控制
                    if (utils.isPc()) {
                        gsap.set(item, {
                            opacity: normalizedZ + 0.2
                        });
                    }

                    if (picsInnerImgs[index]) {
                        gsap.set(picsInnerImgs[index], {
                            scale: normalizedZ * 0.5 + 0.75,
                            duration: 0.3,
                            ease: 'expo.out'
                        });
                    }

                    const distance = Math.abs(normalizedZ - targetOpacity);
                    if (distance < minDistance) {
                        minDistance = distance;
                        newIndex = index;
                    }
                });

                // 只有当索引真正改变时才更新背景图片
                if (newIndex !== -1 && newIndex !== currentIndex) {
                    const prevIndex = currentIndex;
                    currentIndex = newIndex;
                    
                    // console.log(`背景图片切换: ${prevIndex} -> ${currentIndex}`);
                    
                    // 隐藏之前的背景图片
                    if (prevIndex !== -1 && homeBgWorkItems[prevIndex]) {
                        gsap.to(homeBgWorkItems[prevIndex], {
                            opacity: 0,
                            duration: 0.3,
                            ease: 'power2.out'
                        });
                    }
                    
                    // 显示当前的背景图片
                    if (homeBgWorkItems[currentIndex]) {
                        gsap.to(homeBgWorkItems[currentIndex], {
                            opacity: 1,
                            duration: 0.3,
                            ease: 'power2.out'
                        });
                    }
                }
            };

            // 设置粘性容器高度
            const setStickyHeight = () => {
                if (utils.isPc()) {
                    boomStickyContainer.style.setProperty(
                        '--boom-sticky-height', 
                        `${itemCount * utils.calculateVH(100) + utils.calculateVH(180)}px`
                    );
                } else {
                    boomStickyContainer.style.setProperty(
                        '--boom-sticky-height', 
                        `${utils.calculateVH(150) + utils.calculateVH(50) * itemCount}px`
                    );
                }
            };

            setStickyHeight();
            utils.addResizeListener(setStickyHeight);

            // PC端动画
            if (utils.isPc()) {
                //  console.log('创建PC端动画');
                const wrapperWidths = ofWrappers.map(wrapper => wrapper.clientWidth);
                // console.log('wrapperWidths:', wrapperWidths);
                
                // 主滚动动画
                const pcScrollTrigger = ScrollTrigger.create({
                    trigger: '#pics',
                    start: 'top top',
                    end: `+=${itemCount * utils.calculateVH(100)}`,
                    scrub: 0.1,
                    onUpdate: (self) => {
                        let progress = self.progress * 100;
                        progress = gsap.utils.clamp(0, 100, progress);
                        const totalDistance = progress / 100 * depth * itemCount;

                        gsap.set(picsListItems, {
                            z: (index) => (index + 1) * -depth + totalDistance,
                            x: (index) => {
                                if (index === 0) {
                                    const startX = utils.isRTL() ? 
                                        wrapperWidths[index] * 1.25 / 2 : 
                                        (utils.calculateVW(100) - wrapperWidths[index]) / 2;
                                    return gsap.utils.interpolate(startX, 0, gsap.utils.normalize(0, 0.25, self.progress));
                                } else if (index === itemCount - 1) {
                                    const endX = utils.isRTL() ? 
                                        (wrapperWidths[index] * 1.25) / 2 : 
                                       ((utils.calculateVW(100) - wrapperWidths[index]) / 2) + (utils.isPad() ? utils.calculateVW(10) : 0);
                                    return endX;
                                }
                                return 0;
                            }
                        });

                        updateBackgroundImages();
                    }
                });
                // console.log('PC端ScrollTrigger创建成功');

                // 模糊效果动画
                picsListItems.forEach((item, index) => {
                    gsap.fromTo(item, {
                        filter: 'blur(10px)'
                    }, {
                        filter: 'blur(0px)',
                        scrollTrigger: {
                            trigger: '#pics',
                            start: () => `top+=${utils.calculateVH(100) * index} top`,
                            end: () => `+=${utils.calculateVH(25)}`,
                            scrub: 0.1
                        }
                    });
                });
                // console.log('模糊效果动画创建成功');

                // 初始化设置
                (() => {
                    gsap.set(picsListItems, {
                        z: (index) => (index + 1) * -depth,
                        x: (index) => picsListItems[index].clientHeight * 1.25 / 2,
                        zIndex: (index) => index * -1,
                        opacity: 0
                    });
                    
                    // 初始化背景图片状态
                    homeBgWorkItems.forEach((item, index) => {
                        gsap.set(item, { opacity: index === 0 ? 1 : 0 });
                    });
                    currentIndex = 0;
                    
                    updateBackgroundImages();
                })();
                // console.log('初始化设置完成');

                this.animations.set('pics', { scrollTrigger: pcScrollTrigger });
                // console.log('PC端动画注册完成');

            } else {
                // console.log('创建移动端动画');
                // 移动端动画
                const mobileAnimation = createMobileTimeline();
                this.animations.set('pics', mobileAnimation);
                
                // 初始化移动端背景图片状态
                homeBgWorkItems.forEach((item, index) => {
                    gsap.set(item, { opacity: index === 0 ? 1 : 0 });
                });
                currentIndex = 0;
                
                // 初始化移动端元素状态
                (() => {
                    gsap.set(picsListItems, {
                        z: (index) => (index + 1) * -depth,
                        x: (index) => picsListItems[index].clientHeight * 1.25 / 2,
                        zIndex: (index) => index * -1,
                        opacity: 0
                    });
                })();
                
                // console.log('移动端动画注册完成');
            }

            // 背景工作区域动画
            const homeBgWorks = utils.query('.home-bg-works');
            if (homeBgWorks && pics) {
                gsap.timeline({
                    defaults: {
                        ease: 'none'
                    },
                    scrollTrigger: {
                        trigger: sectionBoom,
                        start: () => pics.top(),
                        end: '+=10%',
                        scrub: 0
                    }
                }).fromTo(homeBgWorks, {
                    opacity: 0
                }, {
                    opacity: 1
                });
            }

            // 内容区域动画
            if (picsContent) {
                // console.log('创建 pics-content 动画');
                // console.log('picsContent 元素:', picsContent);
                // console.log('sectionBoom 元素:', sectionBoom);
                
                gsap.timeline({
                    scrollTrigger: {
                        trigger: sectionBoom,
                        start: 'top top',
                        end: 'top top',
                        scrub: true
                    }
                }).fromTo(picsContent, {
                    opacity: 0
                }, {
                    opacity: 1
                });
                
                // console.log('pics-content 动画已创建');
            }

            // 最后遮罩动画
            // gsap.fromTo('.last-mask', {
            //     opacity: 0
            // }, {
            //     opacity: 1,
            //     scrollTrigger: {
            //         trigger: '#section-about',
            //         start: utils.isPc() ? 'top+=20% bottom' : 'top+=50% bottom',
            //         end: '+=20%',
            //         scrub: 0.1
            //     }
            // });

            // 更新进度函数 - 只处理CSS类，不处理透明度
            this.updatePicsProgress = (progress) => {
                const progressPoints = utils.isPc() ? [0, 0.2, 0.4, 0.6, 0.8] : [0.1, 0.3, 0.6, 0.8, 1];
                const currentItemIndex = progress >= 1 ? homeBgWorkItems.length - 1 : 
                    progressPoints.findIndex(point => progress < point) - 1;

                homeBgWorkItems.forEach((item, index) => {
                    utils.toggleClass(item, 'active', index === currentItemIndex);
                });
            };
            // console.log('Pics动画初始化完成');
            // console.log('动画管理器状态:', this.animations);
            
            // 刷新ScrollTrigger
            if (typeof ScrollTrigger !== 'undefined') {
                setTimeout(() => {
                    ScrollTrigger.refresh();
                    //  console.log('ScrollTrigger 已刷新');
                }, 100);
            }
        }

        // 视频切换器动画
        initVideoSwitcher() {
            const videoSwitcher = utils.query('[ani-video-switcher]');
            if (!videoSwitcher) return;

            const videoItem = utils.query('.video-item', videoSwitcher);
            const videoContainer = utils.query('.video-container', videoSwitcher);
            const playBtn = utils.query('.play-btn');
            const playBtnText = utils.query('.play-btn__text');
            const playBtnIcon = utils.query('.play-btn__icon');
            const kvText = utils.query('.kv-text');
            const fadeText = utils.query('.fade-text');
            const playBtnOpacity = utils.query('.play-btn-opacity');

            if (!playBtn) return;

            // 设置播放按钮样式
            const setPlayBtnStyle = () => {
                const padding = 4;
                const gap = utils.isPc() ? 16 : 12;
                const btnWidth = utils.isPc() ? 36 : 28;
                const textWidth = playBtnText?.clientWidth || 0;
                const expandWidth = utils.isPc() ? 145 : btnWidth + gap + textWidth + padding * 2;
                const unexpandWidth = btnWidth + padding * 2 + 2;

                utils.setStyle(playBtn, '--expand-width', `${expandWidth}px`);
                utils.setStyle(playBtn, '--padding', `${padding}px`);
                utils.setStyle(playBtn, '--gap', `${gap}px`);
                utils.setStyle(playBtn, '--btn-width', `${btnWidth}px`);
                utils.setStyle(playBtn, '--unexpand-width', `${unexpandWidth}px`);
                
                if (playBtnOpacity) {
                    utils.setStyle(playBtnOpacity, '--expand-width', `${expandWidth}px`);
                }
            };

            setPlayBtnStyle();

            // 字体加载完成后重新设置样式
            document.fonts.ready.then(setPlayBtnStyle);

            let isVisible = false;
            let isFirstShow = true;

            // 鼠标事件
            if (playBtn) {
                playBtn.addEventListener('mouseenter', () => {
                    if (isVisible !== 'hidden') {
                        utils.addClass(playBtn, 'active');
                    }
                });

                playBtn.addEventListener('mouseleave', () => {
                    if (isVisible !== 'hidden') {
                        utils.removeClass(playBtn, 'active');
                    }
                });
            }

            // 显示/隐藏播放按钮
            const togglePlayBtn = (show) => {
                if (!playBtn) return;

                if (show && isVisible === 'visible' || !show && isVisible === 'hidden') return;

                isVisible = show ? 'visible' : 'hidden';
                gsap.killTweensOf(playBtn);

                if (show) {
                    gsap.timeline()
                        .set(playBtn, {
                            display: 'flex',
                            opacity: 1
                        })
                        .to(playBtn, {
                            duration: 0.1,
                            immediateRender: true
                        }, 0)
                        .to(playBtn, {
                            width: () => playBtn.style.getPropertyValue('--expand-width'),
                            duration: 0.4
                        }, isFirstShow ? 0.5 : 0)
                        .to(playBtnText, {
                            opacity: 1,
                            duration: 0.1
                        }, isFirstShow ? undefined : 0.1);
                } else {
                    gsap.timeline({
                        onStart: () => {
                            if (isFirstShow) isFirstShow = false;
                        }
                    })
                    .to(playBtn, {
                        width: () => playBtn.style.getPropertyValue('--unexpand-width'),
                        duration: 0.2
                    })
                    .to(playBtn, {
                        opacity: 0,
                        duration: 0.2,
                        ease: 'none',
                        onComplete: () => {
                            gsap.set(playBtn, { display: 'none' });
                        }
                    })
                    .to('.play-btn__text', {
                        opacity: 0,
                        duration: 0.1
                    }, '')
                    .set(playBtn, { opacity: 0 });
                }
            };

            // 滚动监听
            const handleScroll = utils.throttle(() => {
                const scrollY = window.scrollY;
                const threshold = utils.calculateVH(25);

                if (scrollY < threshold) {
                    togglePlayBtn(true);
                } else {
                    togglePlayBtn(false);
                }
            }, 16);

            window.addEventListener('scroll', handleScroll);
        }

        // 更新vh值并设置CSS变量
        updateVHValues() {
            const vh = utils.calculateVH(1); // 1vh的像素值
            const vw = utils.calculateVW(1); // 1vw的像素值
            
            // 设置CSS变量
            document.documentElement.style.setProperty('--vh', `${vh}px`);
            document.documentElement.style.setProperty('--vw', `${vw}px`);
            
            // 刷新所有ScrollTrigger
            ScrollTrigger.refresh();
        }

        // 刷新所有动画
        refreshAnimations() {
            this.animations.forEach((animation, key) => {
                if (animation.scrollTrigger) {
                    animation.scrollTrigger.refresh();
                }
            });
        }

        // 确保页面刷新时滚动条在顶部
        ensureScrollToTop() {
            // 方法1: 使用scrollTo
            if (window.scrollTo) {
                window.scrollTo(0, 0);
            }
            
            // 方法2: 设置documentElement的scrollTop
            if (document.documentElement) {
                document.documentElement.scrollTop = 0;
            }
            
            // 方法3: 设置body的scrollTop
            if (document.body) {
                document.body.scrollTop = 0;
            }
            
            // 方法4: 使用history API（如果支持）
            if (window.history && window.history.scrollRestoration) {
                window.history.scrollRestoration = 'manual';
            }
            
            // 监听页面加载完成后的滚动位置
            const ensureTopScroll = () => {
                setTimeout(() => {
                    if (window.scrollY !== 0) {
                        window.scrollTo(0, 0);
                    }
                }, 0);
            };
            
            // 在多个时机确保滚动位置
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', ensureTopScroll);
            } else {
                ensureTopScroll();
            }
            
            // 监听页面可见性变化，确保在页面重新显示时也在顶部
            // document.addEventListener('visibilitychange', () => {
            //     if (!document.hidden) {
            //         setTimeout(() => {
            //             if (window.scrollY !== 0) {
            //                 window.scrollTo(0, 0);
            //             }
            //         }, 100);
            //     }
            // });
        }

        // 销毁动画
        destroy() {
            this.animations.forEach((animation, key) => {
                if (animation.scrollTrigger) {
                    animation.scrollTrigger.kill();
                } else if (animation.kill) {
                    animation.kill();
                }
            });
            this.animations.clear();
        }

        // 性能监控
        checkPerformance() {
            const performance = {
                animations: this.animations.size,
                fps: this.getFPS(),
                memory: this.getMemoryUsage()
            };
            // console.log('Animation Performance:', performance);
            return performance;
        }

        // 获取FPS
        getFPS() {
            let frameCount = 0;
            let lastTime = performance.now();
            
            const countFrames = () => {
                frameCount++;
                const currentTime = performance.now();
                if (currentTime - lastTime >= 1000) {
                    const fps = Math.round((frameCount * 1000) / (currentTime - lastTime));
                    frameCount = 0;
                    lastTime = currentTime;
                    return fps;
                }
                requestAnimationFrame(countFrames);
            };
            
            requestAnimationFrame(countFrames);
        }

        // 获取内存使用情况
        getMemoryUsage() {
            if (performance.memory) {
                return {
                    used: Math.round(performance.memory.usedJSHeapSize / 1048576),
                    total: Math.round(performance.memory.totalJSHeapSize / 1048576),
                    limit: Math.round(performance.memory.jsHeapSizeLimit / 1048576)
                };
            }
            return null;
        }

        // 启用调试模式
        enableDebug() {
            this.debug = true;
            // console.log('Animation debug mode enabled');
            
            // 添加性能监控
            setInterval(() => {
                if (this.debug) {
                    this.checkPerformance();
                }
            }, 5000);
        }
    }

    // 检查并注册 ScrollTrigger 插件
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger);
        // console.log('ScrollTrigger 插件已注册');
    } else {
        // console.error('GSAP 或 ScrollTrigger 未加载');
    }

    // 初始化动画管理器
    const animationManager = new AnimationManager();
    
    // 确保在 DOM 加载完成后初始化
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            // console.log('DOMContentLoaded - 初始化动画管理器');
            animationManager.init();
        });
    } else {
        // console.log('DOM 已加载 - 立即初始化动画管理器');
        animationManager.init();
    }

    // 导出到全局
    window.Brand2025Animation = animationManager;

})(); 